'use client';

/**
 * GettingStarted — landing card for new users with no program selected.
 *
 * Avionics-style intake brief: classification header, 4 mission-option
 * tiles framed as deployment vectors. Each tile is a Link to its
 * destination.
 */

import Link from 'next/link';
import { Dumbbell, ChevronRight } from 'lucide-react';
import { BarbellIcon } from '@/components/icons/BarbellIcon';
import { TacticalChartIcon } from '@/components/icons/TacticalChartIcon';

type Tile = {
  href: string;
  icon: React.ReactNode;
  badge: string;       // OPT-01 / OPT-02 …
  title: string;
  desc: string;
  accent: string;      // tailwind border colour token, e.g. 'amber'
};

const TILES: Tile[] = [
  {
    href: '/programs/change',
    icon: <BarbellIcon className="w-7 h-7" />,
    badge: 'OPT-01 · RECOMMENDED',
    title: 'Start a Program',
    desc: '12-week structured training tailored to your archetype.',
    accent: 'amber',
  },
  {
    href: '/templates',
    icon: <TacticalChartIcon className="w-7 h-7" />,
    badge: 'OPT-02',
    title: 'Quick Template',
    desc: 'One-off workout sessions when you only have today.',
    accent: 'slate',
  },
  {
    href: '/session/freestyle',
    icon: <Dumbbell className="w-7 h-7" />,
    badge: 'OPT-03',
    title: 'Freestyle',
    desc: 'Track your own workout — no plan, your call.',
    accent: 'slate',
  },
];

const ACCENT_MAP: Record<string, { border: string; hover: string; icon: string; badgeTw: string }> = {
  amber: {
    border: 'border-amber-500/40',
    hover: 'hover:border-amber-500/80 hover:bg-amber-500/[0.04]',
    icon: 'text-amber-400',
    badgeTw: 'text-amber-400',
  },
  cyan: {
    border: 'border-hud-cyan/30',
    hover: 'hover:border-hud-cyan/60 hover:bg-hud-cyan/[0.03]',
    icon: 'text-hud-cyan',
    badgeTw: 'text-hud-cyan',
  },
  slate: {
    border: 'border-slate-800',
    hover: 'hover:border-slate-600 hover:bg-slate-900/40',
    icon: 'text-slate-300',
    badgeTw: 'text-slate-500',
  },
};

export default function GettingStarted() {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-hud-ink/85">
      {/* Hairline top accent */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/70 to-transparent" />
      {/* Corner brackets */}
      <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/60" />

      {/* Classification banner */}
      <header className="px-4 py-1.5 border-b border-amber-500/20 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.28em]">
        <div className="flex items-center gap-2 text-amber-500/90">
          <span className="w-1 h-1 rounded-full bg-hud-cyan animate-hud-blink" />
          <span>Intake · Select Deployment</span>
        </div>
        <span className="text-slate-500">{TILES.length} options</span>
      </header>

      <div className="px-4 py-4">
        <h2 className="font-mono text-[16px] font-bold text-white tracking-tight mb-1">
          Choose how to train today.
        </h2>
        <p className="font-mono text-[11px] text-slate-400 leading-relaxed mb-4">
          Pick a path now — you can switch anytime from Settings.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TILES.map((t) => {
            const a = ACCENT_MAP[t.accent];
            return (
              <Link
                key={t.href}
                href={t.href}
                className={`group relative grid grid-cols-[36px_1fr_16px] items-center gap-3 px-3 py-3 rounded-md border bg-slate-950/50 transition ${a.border} ${a.hover}`}
              >
                <div className={`flex-shrink-0 ${a.icon}`}>{t.icon}</div>
                <div className="min-w-0">
                  <div className={`font-mono text-[9px] uppercase tracking-[0.2em] mb-0.5 ${a.badgeTw}`}>
                    {t.badge}
                  </div>
                  <div className="font-mono text-[13px] font-bold text-white leading-snug">
                    {t.title}
                  </div>
                  <div className="font-mono text-[10px] text-slate-500 leading-snug mt-0.5">
                    {t.desc}
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-0.5 ${a.icon}`} aria-hidden />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
